import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import TextField from '@/components/atoms/TextField';
import { DisclosurePanel } from '@/components/molecules/Disclosure';
import { EmptyState } from '@/components/molecules/EmptyState';
import { KeyValueTable } from '@/components/organisms/DataTable';
import { formatDateTimeWorded } from '@/utils/date';
import isObject from '@/utils/isObject';

import { readingLabelKey } from './readingLabels';

import styles from './BioscopeSystemReadings.module.scss';

export interface BioscopeSystemReadingsProps {
    children?: React.ReactNode;
    bioscope?: { id: number }; // Assuming bioscope prop contains an id
    bioscopeData: Record<string, unknown> | null;
    handlePublish: (topic: string, message: string) => void;
}

/** The portal's date and time format, not the browser's, so it follows no other locale. */
function readingTimestamp(value: unknown): string {
    return typeof value === 'number' || typeof value === 'string'
        ? formatDateTimeWorded(value)
        : '';
}

/** What React showed for the raw value; objects, which React cannot render, as JSON. */
function readingText(value: unknown): React.ReactNode {
    if (typeof value === 'string' || typeof value === 'number') return value;
    if (isObject(value) || Array.isArray(value)) return JSON.stringify(value);
    return null;
}

function BioscopeSystemReadings({
    children,
    bioscope,
    bioscopeData,
    handlePublish,
}: BioscopeSystemReadingsProps) {
    // const handleSetLuminosity = () => {
    //     const topic = `bioscope/data/${bioscope?.id}`;
    //     const message = 'fetch_bioscope_data';
    //     handlePublish(topic, message);
    // };

    const { t } = useTranslation();
    const readingLabel = (key: string) => {
        const labelKey = readingLabelKey(key);
        return labelKey ? t(labelKey) : key;
    };
    const [showForm, setShowForm] = React.useState(false);
    const luxFormId = React.useId();
    const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const lux = Number(formData.get('LUX') || 0);
        const topic = `bioscope/set/lux/${bioscope?.id}`;
        const message = JSON.stringify({ lux });
        console.log(`Publishing to ${topic}:`, message);
        handlePublish(topic, message);
        setShowForm(false);
    };

    return (
        <div className={styles.bioscopesystemreadings}>
            <h2>{t('bioscopeDetail.systemReadings.title')}</h2>
            {children}
            <div className={styles.section}>
                <Button
                    variant="secondary"
                    type="button"
                    aria-expanded={showForm}
                    aria-controls={luxFormId}
                    onClick={() => setShowForm((prev) => !prev)}>
                    {showForm
                        ? t('common.actions.cancel')
                        : t('bioscopeDetail.systemReadings.setLux')}
                </Button>
                <DisclosurePanel id={luxFormId} isOpen={showForm}>
                    {showForm && (
                        <form onSubmit={handleFormSubmit} className={styles.luxForm}>
                            <TextField
                                type="number"
                                name="LUX"
                                placeholder={t('bioscopeDetail.systemReadings.luxPlaceholder')}
                                required
                                min="0"
                                max="100"
                                className={styles.luxInput}
                            />

                            <Button type="submit">
                                {t('bioscopeDetail.systemReadings.submit')}
                            </Button>
                        </form>
                    )}
                </DisclosurePanel>
            </div>
            <div className={styles.section}>
                {bioscopeData ? (
                    <KeyValueTable
                        tableClassName={styles.nowrapTable}
                        rows={Object.keys(bioscopeData).map((key) => ({
                            key,
                            label: <strong>{readingLabel(key)}</strong>,
                            value: key.toLowerCase().includes('timestamp')
                                ? readingTimestamp(bioscopeData[key])
                                : readingText(bioscopeData[key]),
                        }))}
                    />
                ) : (
                    <EmptyState title={t('common.labels.noData')} />
                )}
            </div>
        </div>
    );
}

export default BioscopeSystemReadings;
export { BioscopeSystemReadings };
